import type { SupplierRecordTab } from '@crm/widgets/supplier-record-shell';
import type {
  CrmLogEntryType,
  CrmLogTargetEntryType,
} from '@travel-planner/shared/schemas/crm-log';
import { validateAccountsSearch } from '@crm/features/accounts-filters';
import { validateContactsSearch } from '@crm/features/contacts-filters';
import { validateOrphanedCustomersSearch } from '@crm/features/orphaned-customers-filters';
import { validateTourOperatorsSearch } from '@crm/features/tour-operators-filters';
import { isSupplierRecordTab } from '@crm/widgets/supplier-record-shell';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import {
  crmLogEntryTypeValues,
  crmLogTargetEntryTypeValues,
} from '@travel-planner/shared/schemas/crm-log';
import { protectedRoute, redirect } from './_root';

export const crmRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm',
  beforeLoad: () => {
    throw redirect({ to: '/crm/accounts' });
  },
});

export const crmAccountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/accounts',
  validateSearch: validateAccountsSearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/accounts').then((m) => ({
      default: m.CrmAccountsPage,
    }))
  ),
});

export const crmAccountNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/accounts/new',
  component: lazyRouteComponent(() =>
    import('@crm/pages/account-new').then((m) => ({
      default: m.CrmAccountNewPage,
    }))
  ),
});

// Supplier record tabs live in `?pane=` (the product-edit convention — nothing
// in the app uses `?tab=`). An absent or unknown value falls through to the
// default Info tab, which writes no param at all.
type SupplierRecordSearch = { pane?: SupplierRecordTab };

const validateSupplierRecordSearch = (
  input: Record<string, unknown>
): SupplierRecordSearch =>
  isSupplierRecordTab(input.pane) ? { pane: input.pane } : {};

export const crmAccountEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/accounts/$companyId',
  validateSearch: validateSupplierRecordSearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/account-edit').then((m) => ({
      default: m.CrmAccountEditPage,
    }))
  ),
});

export const crmContactsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/contacts',
  validateSearch: validateContactsSearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/contacts').then((m) => ({
      default: m.CrmContactsPage,
    }))
  ),
});

// `?companyId=` marks a contact create/edit opened FROM a company record: the
// company is pre-selected on create, and Back / save return to that record's
// Contacts tab instead of the contacts list.
const validateContactCompanySearch = (
  input: Record<string, unknown>
): { companyId?: string } =>
  typeof input.companyId === 'string' && input.companyId.length > 0
    ? { companyId: input.companyId }
    : {};

export const crmContactNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/contacts/new',
  validateSearch: validateContactCompanySearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/contact-new').then((m) => ({
      default: m.CrmContactNewPage,
    }))
  ),
});

export const crmContactDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/contacts/$contactId',
  component: lazyRouteComponent(() =>
    import('@crm/pages/contact-detail').then((m) => ({
      default: m.CrmContactDetailPage,
    }))
  ),
});

export const crmContactEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/contacts/$contactId/edit',
  validateSearch: validateContactCompanySearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/contact-edit').then((m) => ({
      default: m.CrmContactEditPage,
    }))
  ),
});

export const crmDriversRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/drivers',
  validateSearch: validateTourOperatorsSearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/drivers').then((m) => ({ default: m.CrmDriversPage }))
  ),
});

export const crmDriverNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/drivers/new',
  component: lazyRouteComponent(() =>
    import('@crm/pages/driver-new').then((m) => ({
      default: m.CrmDriverNewPage,
    }))
  ),
});

export const crmDriverEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/drivers/$tourOperatorId',
  component: lazyRouteComponent(() =>
    import('@crm/pages/driver-edit').then((m) => ({
      default: m.CrmDriverEditPage,
    }))
  ),
});

export const crmGuidesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/guides',
  validateSearch: validateTourOperatorsSearch,
  component: lazyRouteComponent(() =>
    import('@crm/pages/guides').then((m) => ({ default: m.CrmGuidesPage }))
  ),
});

export const crmGuideNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/guides/new',
  component: lazyRouteComponent(() =>
    import('@crm/pages/guide-new').then((m) => ({
      default: m.CrmGuideNewPage,
    }))
  ),
});

export const crmGuideEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/guides/$tourOperatorId',
  component: lazyRouteComponent(() =>
    import('@crm/pages/guide-edit').then((m) => ({
      default: m.CrmGuideEditPage,
    }))
  ),
});

// Layout templates. Moved out of /system-configuration/layout-templates, whose
// only entry point was the admin gear menu that non-admins never render — see
// the tab comment in apps/web/src/widgets/app-layout/lib/crm-tabs.ts. The old
// path still resolves: it redirects here (apps/web/src/app/routes/settings.ts).
export const crmLayoutsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/layouts',
  component: lazyRouteComponent(() =>
    import('@crm/pages/crm-layouts').then((m) => ({
      default: m.CrmLayoutsPage,
    }))
  ),
});

// ─── CRM Logs (inline validateSearch) ────────────────────────────

type CrmLogsSearch = {
  entryType?: CrmLogEntryType;
  targetEntity?: CrmLogTargetEntryType;
};

const isEntryType = (v: unknown): v is CrmLogEntryType =>
  typeof v === 'string' &&
  (crmLogEntryTypeValues as readonly string[]).includes(v);
const isTargetEntity = (v: unknown): v is CrmLogTargetEntryType =>
  typeof v === 'string' &&
  (crmLogTargetEntryTypeValues as readonly string[]).includes(v);

const validateCrmLogsSearch = (
  input: Record<string, unknown>
): CrmLogsSearch => ({
  entryType: isEntryType(input.entryType) ? input.entryType : undefined,
  targetEntity: isTargetEntity(input.targetEntity)
    ? input.targetEntity
    : undefined,
});

export const crmLogsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/logs',
  component: lazyRouteComponent(() =>
    import('@crm/pages/crm-logs').then((m) => ({ default: m.CrmLogsPage }))
  ),
  validateSearch: validateCrmLogsSearch,
});

// XT-724 phase 2 — the duplicate-account review queue. No search params: the
// filter is local page state, because a group key is a list of row ids and
// putting that in the URL would outlive the group the moment one is merged.
export const crmDuplicatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/duplicates',
  component: lazyRouteComponent(() =>
    import('@crm/pages/duplicates').then((m) => ({
      default: m.CrmDuplicatesPage,
    }))
  ),
});

export const crmOrphanedCustomersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'crm/orphaned-customers',
  component: lazyRouteComponent(() =>
    import('@crm/pages/orphaned-customers').then((m) => ({
      default: m.CrmOrphanedCustomersPage,
    }))
  ),
  validateSearch: validateOrphanedCustomersSearch,
});
